/*
    AppState.js -- Global state
 */

import {State, allow, blend, clone, expression, getModels, getVuetify} from '@/paks/vu-app'
import {Routes} from '@/paks/vu-routes'
import {Log} from '@/paks/js-log'
import {cleanDisplay} from '@/paks/vu-dash/BoardUtils.js'

const PersistState = [
    'dark',
    'initialized',
    'notices',
    'prefers',
    'signs',
    'showNestedPages',
    'tab',
    'themeMode',
    'upgradeAttempt',
    'wizardsCompleted',
]

export default class AppState {
    api = null
    app = null
    /*
        Set to true by panel or page when a back button is needed on mobile
        Set to false by navbar when back button is pressed
        Set to "back" to show back button in the navbar
        Set to null to clear
     */
    back = null
    closing = null
    cognito = null
    dark = null
    dispatcher = null
    display = null
    doc = {}
    fullDisplay = null
    help = null
    initialized = false
    lastAccess = new Date()
    local = false
    location = {
        hash: null,
        prior: null,
        query: {},
        url: null,
    }
    logging = null
    logo = null
    logFormat = 'plain'
    maintenance = null
    mobile = false
    mock = false
    needs = {}
    nextTable = 1
    notices = {}
    prefers = {}
    refresh = null
    rendered = false
    region = null
    refresh = null
    schema = null
    showNestedPages = false
    signs = {}
    social = null
    started = new Date()
    support = null
    tab = {}
    themeMode = 'auto'
    unsaved = null
    upgrading = false
    upgradeAttempt = 0
    useMap = {}
    versions = {}
    widgetSets = []
    wizardActive = null
    wizardLaunch = null
    wizardsCompleted = {}

    constructor() {
        this.lastCheck = new Date(0)
    }

    saveState(state) {
        return allow(state, PersistState)
    }

    loadState(state) {
        state = allow(state, PersistState)
        if (state.notices) {
            for (let [key, value] of Object.entries(state.notices)) {
                if (typeof value != 'object') {
                    state.notices[key] = {show: value, dismiss: !value}
                }
            }
        }
        state.location = state.location || {}
        state.location.query = {}
        window.addEventListener('resize', this.resizeWindow.bind(this))
        return state
    }

    activateWizard(active) {
        this.wizardActive = active
    }

    async closeAccount() {
        let Account = getModels('Account')
        try {
            this.closing = true
            await Account.close()
        } catch (err) {
            Log.error('Cannot close account', {err})
        } finally {
            this.closing = false
        }
    }

    completeWizard(name, complete = new Date()) {
        this.wizardsCompleted[name] = complete
    }

    dismissSign(name) {
        this.signs[name] = this.signs[name] || {}
        this.signs[name].dismiss = true
    }

    // UNUSED
    dispatched() {
        this.dispatcher = new Date()
    }

    get display() {
        return this.display
    }

    getInvite() {
        return this.location.query.invite
    }

    getNextTable() {
        return this.nextTable++
    }

    getSign(name) {
        return (this.signs[name] = this.signs[name] || {})
    }

    get isSocial() {
        let loc = this.location || '/'
        return loc.url.indexOf('/login/social') == 0 ? true : false
    }

    launchWizard() {
        this.wizardLaunch = new Date()
    }

    /* 
        Pick a display from the full display list
        If there is not displays[] list, then return the full display
        If no display matches, then return the first display
        If a display has no expression, then it is selected by default
     */
    pickDisplay(fullDisplay = this.fullDisplay) {
        if (fullDisplay.displays) {
            for (let display of fullDisplay.displays) {
                let enable = true
                if (display.expression != null) {
                    enable = expression(display.expression, {nowait: true})
                }
                if (enable) {
                    return display
                }
            }
            console.log('No matching display found, picking first')
            return fullDisplay.displays[0]
        } else {
            console.log('Legacy display format')
            return fullDisplay
        }
    }

    prefer(key, defaultValue) {
        let value = this.prefers[key]
        if (!value) {
            value = defaultValue
        }
        if (!value) {
            if (key == 'cloudId') {
                value = State.cache.clouds.filter((c) => c.type != 'host')[0]?.id
            } else if (key == 'productId') {
                value = State.cache.products[0]?.id
            }
        }
        if (value) {
            if (key == 'cloudId') {
                if (!State.cache.clouds.find((c) => c.id == value)) {
                    value = State.cache.clouds[0]?.id
                }
            } else if (key == 'productId') {
                if (!State.cache.products.find((p) => p.id == value)) {
                    value = State.cache.products[0]?.id
                }
            }
        }
        return value
    }

    resetNotices() {
        for (let [key, value] of Object.entries(this.notices)) {
            value.show = null
            value.dismiss = null
        }
        for (let [key, value] of Object.entries(this.signs)) {
            value.show = null
            value.dismiss = null
        }
    }

    rebase(path, parent) {
        if (!path || path[0] == '/' || !parent) {
            return path
        }
        if (parent.path == '/') {
            return `/${path}`
        } else {
            return `${parent.path}/${path}`
        }
    }

    rebaseViews(parent) {
        let views = (parent.views || []).concat(parent.children || [])
        for (let view of views) {
            view.path = this.rebase(view.path, parent)
            if (view.redirect) {
                view.redirect = this.rebase(view.redirect, view)
            }
            if (view.views || view.children) {
                this.rebaseViews(view)
            }
        }
    }

    resizeWindow() {
        if (!this.resizing) {
            this.resizing = setTimeout(async () => {
                let display = this.pickDisplay()
                this.setDisplay(display)
                this.resizing = null
            }, 500)
        }
    }

    setActivity() {
        this.lastAccess = new Date()
    }

    setApi(api) {
        if (!api) {
            api = `${window.location.host}/api`
        }
        this.api = api
    }

    setApp(app) {
        this.app = app
    }

    setCognito(cognito) {
        this.cognito = cognito
    }

    setDisplay(display) {
        display = cleanDisplay(display, true)
        this.display = display
        if (display.features) {
            this.setFeatures(display.features)
        }
        if (display.logo) {
            this.setLogo(display.logo)
        }
        if (display.theme) {
            this.setTheme(display.theme)
        }
        // DEPRECATED
        if (display.themes) {
            this.setTheme(display.themes)
        }
        if (display.title) {
            this.setTitle(display.title)
        }
        if (display.version) {
            State.config.version = display.version
        }
        if (display.timeouts) {
            State.config.timeouts = Object.assign(State.config.timeouts || {}, display.timeouts)
        }
        this.mobile = window.innerWidth <= 640
        this.rebaseViews(display)
        Routes.replaceRoutes(display)

        let router = State.ref.router
        router.replace(router.currentRoute.value.fullPath)
        Log.info(`Selected display "${display.name}" using "${display.expression}"`)
    }

    setFeatures(features) {
        if (features?.dash?.enable == null) {
            features.dash.enable = true
        }
        State.config.features = blend(State.config.features || {}, features)
        if (State.config.features?.auth?.social) {
            this.setSocial(State.config.features.auth.social)
        }
    }

    async setFullDisplay(fullDisplay) {
        // REPAIR
        for (let display of fullDisplay.displays) {
            if (display.themes) {
                display.theme = display.themes
                delete display.themes
            }
            let theme = display.theme
            // REPAIR
            if (theme.modes) {
                let formats = theme.formats
                theme = theme.modes
                for (let mode of Object.keys(theme)) {
                    theme[mode].formats = formats
                }
                display.theme = theme
            }
        } // REPAIR
        this.fullDisplay = fullDisplay
        let display = this.pickDisplay(fullDisplay)
        this.setDisplay(display)
    }

    /*
            Set the url for context sensitive help
         */
    setHelp(url) {
        this.help = url
    }

    setInvite(value) {
        return (this.location.query.invite = value)
    }

    setLogo(logo) {
        this.logo = logo
    }

    need(key) {
        return this.needs[key]
    }

    noNeed(key) {
        delete this.needs[key]
    }

    setNeed(key, value = Date.now()) {
        this.needs[key] = value
    }

    setNeedSave(board, value = Date.now()) {
        if (board.name && board.type == 'dashboard') {
            let save = this.needs.save || {}
            save[board.name] = value
            this.needs.save = save
        } else {
            this.setNeed(`deploy`, value)
        }
    }

    setPrefer(key, value) {
        this.prefers[key] = value
    }

    setRendered() {
        this.rendered = true
    }

    setSchema(schema) {
        this.schema = schema
    }

    setSocial(social) {
        this.social = social
    }

    setStandardDisplay(display) {
        this.standardDisplay = clone(display)
        for (let display of this.standardDisplay.displays) {
            this.rebaseViews(display)
        }
    }

    setTheme(theme) {
        // REPAIR
        if (theme.modes) {
            let formats = theme.formats
            theme = theme.modes
            for (let mode of Object.keys(theme)) {
                theme[mode].formats = formats
            }
        }
        // Apply references to other colors. i.e. expand colors that refer to others such as "primary"
        for (let mode of Object.values(theme)) {
            for (let [color, value] of Object.entries(mode.colors)) {
                if (value == 'none') {
                    value = 'background'
                }
                if (mode.colors[value]) {
                    mode.colors[color] = mode.colors[value]
                }
                // REPAIR
                if (!mode.colors.navbar) {
                    mode.colors.navbar = 'primary'
                }
                if (!mode.colors.sidebar) {
                    mode.colors.sidebar = 'secondary'
                }
            }
        }
        State.config.theme = theme
        let vthemes = getVuetify().theme.themes.value
        for (let mode of Object.keys(theme)) {
            if (mode == 'dark' || mode == 'light') {
                vthemes[mode].colors = theme[mode].colors
                vthemes[mode].dark = mode == 'dark'
                Object.assign(vthemes[mode].variables, theme[mode].variables)
            }
        }
        this.setThemeMode(null, true)
    }

    /*
        Set the theme mode. If persist is true, then the theme mode is persisted in the state.
        State.app.themeMode will be light, dark or auto.
        State.app.dark will be true if dark mode is active.
     */
    setThemeMode(newMode, persist = false) {
        newMode = newMode || State.app.themeMode || 'auto'
        let themeMode = newMode
        if (!themeMode || themeMode == 'auto') {
            themeMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        }
        getVuetify().theme.change(themeMode)
        State.app.dark = themeMode == 'dark'

        let theme = State.config.theme
        let mode = theme[themeMode] || theme.light
        this.setLogo(mode.logo || '/images/logo.png')
        if (persist || State.app.themeMode == null) {
            State.app.themeMode = newMode || themeMode
        }
    }

    setTitle(title) {
        this.title = title
    }

    //  Versions of various components. Currently only using versions.cloud
    setVersions(versions) {
        this.versions = versions || {}
    }

    get suspended() {
        return State.auth?.account?.suspended
    }
}
